import { useState } from 'react';
import { RunButton, InfoCard, NoResult, AiAdvisor } from './common';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { APP } from '@/constants/testIds';
import { toast } from 'sonner';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine,
} from 'recharts';

const Growth = ({ farm, selectedCrop, crops, year, season }) => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const label = crops.find(c => c.key === selectedCrop)?.label;

  const run = async () => {
    setLoading(true); setErrMsg('');
    try {
      setResult(await api.growth({
        crop_key: selectedCrop,
        lat: farm.lat, lon: farm.lon,
        polygon: farm.parcel_ring || null,
        include_ndvi: true,
        ndvi_lookback_days: 90,
        year, season,
      }));
    }
    catch (e) {
      setErrMsg(e?.response?.data?.detail || 'Growth monitor failed.');
      toast.error('Growth monitor failed');
    }
    finally { setLoading(false); }
  };

  const ndvi = result?.ndvi;
  const isLiveNdvi = ndvi?.source?.startsWith('live_gee');

  return (
    <InfoCard title={`Crop Growth Monitoring · ${label}`}
              subtitle="Tracks days-after-sowing, cumulative GDD, current growth stage, and — from Sentinel-2 imagery — the NDVI time-series & anomaly vs. the previous cloud-free image (scene cloud < 20%).">
      <RunButton testId={APP.actions.runGrowth} onClick={run} loading={loading}>Check progress</RunButton>
      {errMsg && (
        <div className="mt-4 p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-800">
          <b>Weather feed unavailable:</b> {errMsg}. Growth tracking uses Open-Meteo for GDD — please retry shortly.
        </div>
      )}
      {!result && !loading && !errMsg && <div className="mt-4"><NoResult /></div>}
      {result && (
        <div className="mt-5 space-y-6 animate-fade-up">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kv l="Sowing date" v={result.sowing_date} />
            <Kv l="DAS today" v={`${result.das} / ${result.duration_days}`} />
            <Kv l="Cum GDD" v={`${result.cum_gdd} °C·d`} />
            <Kv l="Stage" v={result.stage} />
          </div>
          <div>
            <div className="flex justify-between text-xs text-icrisat-earth mb-1">
              <span>Season progress</span>
              <span>{result.progress_pct}%</span>
            </div>
            <Progress value={result.progress_pct} className="h-3" />
          </div>

          {ndvi && <NdviPanel ndvi={ndvi} isLive={isLiveNdvi} />}

          <AiAdvisor context="growth" payload={result} />
        </div>
      )}
    </InfoCard>
  );
};

const NdviPanel = ({ ndvi, isLive }) => {
  const series = (ndvi.time_series || []).map(r => ({ ...r, ndvi: r.ndvi }));
  const anomaly = ndvi.anomaly;
  const latest = ndvi.latest;
  const previous = ndvi.previous;
  const trend = anomaly?.mean_diff;
  const trendColor = trend == null ? 'gray'
                    : trend > 0.02 ? 'emerald'
                    : trend < -0.02 ? 'rose'
                    : 'amber';

  return (
    <div className="rounded-xl border border-icrisat-green/20 bg-gradient-to-b from-white to-icrisat-green-soft/40 p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-display text-icrisat-green text-lg">NDVI · Sentinel-2 (cloud &lt; {ndvi.cloud_threshold_pct}%)</div>
          <div className="text-xs text-icrisat-earth">
            {ndvi.n_scenes ?? 0} scenes · last {ndvi.lookback_days} days
          </div>
        </div>
        <Badge variant={isLive ? 'default' : 'outline'}
               className={isLive ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-50 border-amber-300 text-amber-800'}>
          {isLive ? 'LIVE · GEE' : 'SYNTHETIC'}
        </Badge>
      </div>

      {ndvi.warning && !isLive && (
        <div className="mb-3 p-2 rounded-md bg-amber-50 border border-amber-200 text-[11px] text-amber-800">
          NDVI is running in synthetic mode. Reason: {ndvi.warning}
        </div>
      )}
      {ndvi.thumbnail_note && (
        <div className="mb-3 p-2 rounded-md bg-sky-50 border border-sky-200 text-[11px] text-sky-800">
          <b>Live NDVI stats loaded</b> — but thumbnail images unavailable: {ndvi.thumbnail_note}
        </div>
      )}

      {/* Time-series chart */}
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={series} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
            <YAxis domain={[0, 1]} tick={{ fontSize: 10 }} />
            <Tooltip contentStyle={{ fontSize: 12 }} />
            <ReferenceLine y={0.3} stroke="#eab308" strokeDasharray="4 4" label={{ value: 'bare/emerging', fontSize: 10, position: 'insideTopLeft' }} />
            <ReferenceLine y={0.6} stroke="#16a34a" strokeDasharray="4 4" label={{ value: 'canopy closed', fontSize: 10, position: 'insideTopLeft' }} />
            <Line type="monotone" dataKey="ndvi" stroke="#166534" strokeWidth={2}
                  dot={{ r: 3, fill: '#166534' }} activeDot={{ r: 5 }} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Latest / previous / anomaly cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
        <NdviTile title={`Latest · ${latest?.date || '—'}`}
                  meanLabel="Mean NDVI"
                  mean={latest?.ndvi_mean}
                  thumb={latest?.thumb_url} palette="ndvi" />
        <NdviTile title={`Previous · ${previous?.date || '—'}`}
                  meanLabel="Mean NDVI"
                  mean={previous?.ndvi_mean}
                  thumb={previous?.thumb_url} palette="ndvi" />
        <NdviTile title={`Anomaly · Δ vs ${anomaly?.vs_date || '—'}`}
                  meanLabel="Mean ΔNDVI"
                  mean={anomaly?.mean_diff}
                  thumb={anomaly?.thumb_url} palette="anomaly"
                  trendColor={trendColor} />
      </div>

      <div className="mt-3 text-[11px] text-muted-foreground leading-relaxed">
        NDVI ranges 0 → 1. Values &lt; 0.30 = bare soil / early emergence · 0.30 – 0.60 = development · &gt; 0.60 = full canopy.
        A negative ΔNDVI between consecutive cloud-free images may indicate <b>water stress, pest / disease pressure, or harvest</b>. Cross-check with the Irrigation and Nutrient tabs before acting.
      </div>
    </div>
  );
};

const NdviTile = ({ title, meanLabel, mean, thumb, palette, trendColor }) => (
  <div className="rounded-lg bg-white border border-icrisat-green/15 p-3">
    <div className="text-[10px] uppercase text-icrisat-earth">{title}</div>
    <div className="font-display text-icrisat-green text-xl mt-1">
      {meanLabel}: {mean != null ? mean.toFixed(3) : '—'}
      {trendColor && mean != null && (
        <span className={`ml-2 text-xs px-2 py-0.5 rounded-full
          ${trendColor === 'emerald' ? 'bg-emerald-100 text-emerald-800' :
            trendColor === 'rose'    ? 'bg-rose-100 text-rose-800' :
            trendColor === 'amber'   ? 'bg-amber-100 text-amber-800' :
                                        'bg-slate-100 text-slate-700'}`}>
          {mean > 0 ? '↑ greening' : mean < 0 ? '↓ browning' : 'stable'}
        </span>
      )}
    </div>
    {thumb ? (
      <img src={thumb} alt={title}
           className="mt-2 w-full aspect-square object-cover rounded-md border border-icrisat-green/20" />
    ) : (
      <div className="mt-2 w-full aspect-square rounded-md border border-dashed border-icrisat-green/30
                      flex items-center justify-center text-[11px] text-icrisat-earth bg-icrisat-green-soft/30 p-3 text-center">
        Live satellite thumbnail available only when Google Earth Engine credentials & permissions are configured.
      </div>
    )}
    <PaletteLegend palette={palette} />
  </div>
);

const PaletteLegend = ({ palette }) => {
  const ndviStops = ['#d73027', '#fdae61', '#ffffbf', '#a6d96a', '#1a9850'];
  const anomStops = ['#8c510a', '#dfc27d', '#f5f5f5', '#80cdc1', '#01665e'];
  const stops = palette === 'anomaly' ? anomStops : ndviStops;
  const lo = palette === 'anomaly' ? '−0.3' : '0';
  const mid = palette === 'anomaly' ? '0' : '0.5';
  const hi = palette === 'anomaly' ? '+0.3' : '1';
  return (
    <div className="mt-2 flex items-center gap-2 text-[10px] text-icrisat-earth">
      <div className="h-2 flex-1 rounded" style={{
        background: `linear-gradient(90deg, ${stops.join(', ')})`,
      }} />
      <span>{lo}</span><span>·</span><span>{mid}</span><span>·</span><span>{hi}</span>
    </div>
  );
};

const Kv = ({ l, v }) => (
  <div className="rounded-lg bg-icrisat-green-soft/70 border border-icrisat-green/20 p-3">
    <div className="text-[10px] uppercase text-icrisat-earth">{l}</div>
    <div className="font-display text-icrisat-green">{v ?? '—'}</div>
  </div>
);

export default Growth;
